import { Component, DoCheck, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Observable, Subscription } from 'rxjs';
import { BreadcrumbService } from 'src/app/app.breadcrumb.service';
import { AbstractService } from 'src/app/services/abstract.service';
import { RelatorioDinamicoService } from 'src/app/services/relatorio-dinamico.service';
import { MensagemIa } from '../ia/conversa-ia.component';
import { resumirProposta, rodapeUso } from '../ia/resumo-proposta';

type Aba = 'dados' | 'filtros' | 'colunas' | 'agrupamentos' | 'indicadores' | 'graficos' | 'layout' | 'distribuicao' | 'versoes' | 'json';

interface Opcao {
  label: string;
  value: any;
}

const NOMES_TOTAL: { [k: string]: string } = { soma: 'Soma', media: 'Média', contagem: 'Contagem', distintos: 'Distintos (conta sem repetir)', min: 'Mínimo', max: 'Máximo', grupo: 'Soma, uma vez por grupo' };
const NOMES_GRAFICO: { [k: string]: string } = {
  barras: 'Barras',
  barrasHorizontais: 'Barras deitadas',
  linha: 'Linha',
  area: 'Área',
  pizza: 'Pizza',
  rosca: 'Rosca',
};
const NUMERICOS = ['inteiro', 'decimal', 'moeda', 'percentual'];
const DATAS = ['data', 'dataHora'];
const CATEGORIA_POR_GRAFICO: { [tipo: string]: string[] } = {
  barras: ['texto', 'booleano', 'inteiro', ...DATAS],
  barrasHorizontais: ['texto', 'booleano', 'inteiro', ...DATAS],
  linha: [...DATAS, 'inteiro'],
  area: [...DATAS, 'inteiro'],
  pizza: ['texto', 'booleano'],
  rosca: ['texto', 'booleano'],
};
const COLUNA_POR_FUNCAO: { [funcao: string]: string[] } = {
  SUM: NUMERICOS,
  AVG: NUMERICOS,
  MIN: [...NUMERICOS, ...DATAS],
  MAX: [...NUMERICOS, ...DATAS],
};
const NOMES_PERIODO: { [k: string]: string } = {
  mesAtual: 'Mês atual',
  mesAnterior: 'Mês anterior',
  hoje: 'Hoje',
  ontem: 'Ontem',
  semanaAtual: 'Semana atual',
  ultimos7: 'Últimos 7 dias',
  ultimos30: 'Últimos 30 dias',
  anoAtual: 'Ano atual',
};

@Component({
  selector: 'habil-modelo-editor',
  templateUrl: './modelo-editor.component.html',
  styleUrls: ['./modelo-editor.component.scss'],
  providers: [MessageService, ConfirmationService],
})
export class ModeloEditorComponent implements OnInit, DoCheck, OnDestroy {
  id: number;
  info: any = null;
  ocupado = '';
  aba: Aba = 'colunas';

  abas: { id: Aba; nome: string; breve?: boolean; contador?: () => number }[] = [
    { id: 'dados', nome: 'Dados' },
    { id: 'filtros', nome: 'Filtros', contador: () => this.modelo?.filtros?.length || 0 },
    { id: 'colunas', nome: 'Colunas', contador: () => this.modelo?.colunas?.length || 0 },
    { id: 'agrupamentos', nome: 'Agrupamentos e totais', contador: () => this.modelo?.agrupamentos?.length || 0 },
    { id: 'indicadores', nome: 'Indicadores', contador: () => this.modelo?.indicadores?.length || 0 },
    { id: 'graficos', nome: 'Gráficos', contador: () => this.modelo?.graficos?.length || 0 },
    { id: 'layout', nome: 'Layout' },
    { id: 'distribuicao', nome: 'Distribuição' },
    { id: 'versoes', nome: 'Versões', contador: () => this.versoes.length },
    { id: 'json', nome: 'JSON' },
  ];

  readonly vazio: any[] = [];
  modelo: any = null;
  modeloTexto = '';
  jsonInvalido = '';
  categoriaId: number = null;
  opcoesCategoria: Opcao[] = [];
  schemaModelo: any = null;

  fontes: any[] = [];
  colunasFonte: any[] = [];
  opcoesColuna: Opcao[] = [];
  variaveisFonte: { nome: string; rotulo: string; descricao: string }[] = [];
  opcoesVariavel: Opcao[] = [];
  opcoesCategoriaGrafico: { [tipo: string]: Opcao[] } = {};
  colunasComValor: string[] = [];
  filtrosFixosProprios = false;
  opcoesColunaMetrica: { [funcao: string]: Opcao[] } = {};
  colunasForaDoRelatorio: Opcao[] = [];
  colunasSemOrdenacao: Opcao[] = [];
  parametrosForaDosFiltros: any[] = [];
  colunasSemFiltro: Opcao[] = [];
  opcoesNivel: Opcao[] = [];
  opcoesOrdenarGrupo: Opcao[] = [];
  opcoesParametroPor: { [chave: string]: Opcao[] } = {};
  opcoesIndicadorPor: Opcao[][] = [];
  layoutEfetivo: any[] = [];
  private estadoDerivados = '';
  opcoesFonte: Opcao[] = [];
  buscaColuna = '';
  validacao: { erros: string[]; avisos: string[] } = null;

  resultado: any = null;
  valoresPrevia: { [chave: string]: any } = {};
  mostrarPrevia = false;
  mostrarIa = false;
  mensagensIa: MensagemIa[] = [];
  historicoIa: { papel: string; texto: string }[] = [];
  pensandoIa = false;
  propostaIa: any = null;
  resumoIa: { rotulo: string; valor: string }[] = [];
  rodandoPrevia = false;
  erroPrevia = '';
  private temporizadorPrevia: any;
  previaGrupos: { nivel: number; nome: string; extra: string; icone: string }[] = [];
  private inscricaoPrevia: Subscription;
  private sequenciaPrevia = 0;
  private ultimoEstado = '';
  private textoModelo = '';
  private chaveOpcoes = '';
  private estadoSalvo = '';

  distribuicao = { distribuicao: 'TODOS', rotinas: [] as string[], exigirTodasRotinas: false, cnpjs: [] as string[], softwares: [] as string[] };
  rotinas: Opcao[] = [];
  softwares = ['HABIL_10', 'LIMBER_CHEF', 'HABIL_EMPRESARIAL', 'HABIL_PLAY', 'HABIL_SPA', 'HABIL_FRANQUEADORA'];
  opcoesExigir = [
    { label: 'qualquer uma', value: false },
    { label: 'todas', value: true },
  ];

  versoes: any[] = [];
  versaoVista: any = null;
  mostrarVersao = false;

  mostrarPublicar = false;
  publicacao = { piloto: false, cnpjsPiloto: [] as string[], notas: '' };

  podeEditar = false;
  podePublicar = false;
  podeExcluir = false;

  opcoesTotal: Opcao[] = [{ label: 'Nenhum', value: null }, ...Object.keys(NOMES_TOTAL).map((k) => ({ label: NOMES_TOTAL[k], value: k }))];
  opcoesFormato: Opcao[] = [
    { label: 'Do tipo da coluna', value: null },
    { label: 'Moeda (R$)', value: 'moeda' },
    { label: 'Decimal', value: 'decimal' },
    { label: 'Inteiro', value: 'inteiro' },
    { label: 'Percentual', value: 'percentual' },
    { label: 'Data', value: 'data' },
    { label: 'Data e hora', value: 'dataHora' },
    { label: 'CPF/CNPJ', value: 'cpfCnpj' },
    { label: 'CEP', value: 'cep' },
    { label: 'Telefone', value: 'telefone' },
    { label: 'Texto', value: 'texto' },
  ];
  opcoesDirecao: Opcao[] = [
    { label: 'Maior primeiro', value: 'DESC' },
    { label: 'Menor primeiro', value: 'ASC' },
  ];
  opcoesDirecaoLinha: Opcao[] = [
    { label: 'Crescente', value: 'ASC' },
    { label: 'Decrescente', value: 'DESC' },
  ];
  opcoesAoAbrir: Opcao[] = [
    { label: 'Aberto', value: false },
    { label: 'Recolhido', value: true },
  ];
  opcoesPeriodo: Opcao[] = Object.keys(NOMES_PERIODO).map((k) => ({ label: NOMES_PERIODO[k], value: k }));
  opcoesPeriodoTeste: Opcao[] = [...this.opcoesPeriodo, { label: 'Escolher datas', value: 'datas' }];
  opcoesBooleano: Opcao[] = [
    { label: 'Sim', value: true },
    { label: 'Não', value: false },
  ];
  opcoesFuncao: Opcao[] = [
    { label: 'Soma', value: 'SUM' },
    { label: 'Contagem de linhas', value: 'COUNT' },
    { label: 'Contagem de distintos', value: 'COUNT_DISTINCT' },
    { label: 'Média', value: 'AVG' },
    { label: 'Mínimo', value: 'MIN' },
    { label: 'Máximo', value: 'MAX' },
  ];
  opcoesComparar: Opcao[] = [
    { label: 'Não comparar', value: null },
    { label: 'Período anterior', value: 'periodoAnterior' },
    { label: 'Mesmo período do ano anterior', value: 'mesmoPeriodoAnoAnterior' },
  ];
  opcoesTipoGrafico: Opcao[] = Object.keys(NOMES_GRAFICO).map((k) => ({ label: NOMES_GRAFICO[k], value: k }));
  opcoesAgruparData: Opcao[] = [
    { label: 'Por dia', value: 'dia' },
    { label: 'Por semana', value: 'semana' },
    { label: 'Por mês', value: 'mes' },
    { label: 'Por ano', value: 'ano' },
  ];
  opcoesOrdemGrafico: Opcao[] = [
    { label: 'Automática', value: null },
    { label: 'Maior valor primeiro', value: 'valor' },
    { label: 'Pela categoria', value: 'categoria' },
  ];
  opcoesPosicaoIndicadores: Opcao[] = [
    { label: 'No início', value: 'inicio' },
    { label: 'No final', value: 'final' },
  ];
  opcoesLargura: Opcao[] = [
    { label: 'Inteira', value: 'inteira' },
    { label: 'Metade', value: 'metade' },
  ];
  opcoesTipoIndicador: Opcao[] = [
    { label: 'Métrica', value: 'metrica' },
    { label: 'Variável', value: 'variavel' },
    { label: 'Divisão', value: 'calculo' },
  ];

  constructor(
    public service: RelatorioDinamicoService,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private breadcrumbService: BreadcrumbService,
    private zona: NgZone,
    titleService: Title
  ) {
    titleService.setTitle('Modelo de relatório - ' + AbstractService.app);
  }

  @ViewChild('painelFonte') set painelFonte(ref: ElementRef<HTMLElement>) {
    this.elementoPainelFonte = ref?.nativeElement || null;
    setTimeout(() => this.ajustarPainelFonte());
  }

  private elementoPainelFonte: HTMLElement | null = null;
  private quadroPendente = 0;
  private readonly aoMexerNaTela = () => {
    if (!this.quadroPendente) {
      this.quadroPendente = requestAnimationFrame(() => {
        this.quadroPendente = 0;
        this.ajustarPainelFonte();
      });
    }
  };

  private ajustarPainelFonte(): void {
    const el = this.elementoPainelFonte;
    if (!el) {
      return;
    }
    if (window.innerWidth <= 1100) {
      el.style.maxHeight = '';
      return;
    }
    const topo = Math.max(el.getBoundingClientRect().top, 16);
    el.style.maxHeight = `${Math.max(window.innerHeight - topo - 16, 280)}px`;
  }

  ngOnInit(): void {
    this.zona.runOutsideAngular(() => {
      document.addEventListener('scroll', this.aoMexerNaTela, true);
      window.addEventListener('resize', this.aoMexerNaTela);
    });
    this.podeEditar = this.service.pode('E');
    this.podePublicar = this.service.pode('P');
    this.podeExcluir = this.service.pode('D');
    this.service.schema().subscribe((s) => (this.schemaModelo = { ...s, oneOf: undefined, $ref: '#/definitions/modelo' }));
    this.service.fontesParaModelo().subscribe((f) => {
      this.fontes = f;
      this.opcoesFonte = f.map((x) => ({ label: `${x.titulo} (${x.chave} v${x.versao})`, value: x.chave }));
    });
    this.service.rotinas().subscribe((r) => (this.rotinas = r.map((x) => ({ label: `${x.description} (${x.value})`, value: x.value }))));
    this.service.categorias().subscribe((c) => (this.opcoesCategoria = this.achatarCategorias(c)));
    this.route.queryParams.subscribe((p) => {
      this.id = Number(p.id);
      this.carregar();
    });
  }

  ngDoCheck(): void {
    if (!this.modelo) {
      return;
    }
    this.textoModelo = JSON.stringify(this.modelo);
    const derivados = this.textoModelo + '|' + this.buscaColuna + '|' + this.fontes.length;
    if (derivados !== this.estadoDerivados) {
      this.estadoDerivados = derivados;
      this.recalcular();
    }
    const estado = this.textoModelo + '|' + JSON.stringify(this.valoresPrevia) + '|' + (this.service.base?.descricao || '');
    if (estado !== this.ultimoEstado) {
      const primeira = !this.ultimoEstado;
      this.ultimoEstado = estado;
      if (!primeira) {
        this.agendarPrevia(800);
      }
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.aoMexerNaTela, true);
    cancelAnimationFrame(this.quadroPendente);
    window.removeEventListener('resize', this.aoMexerNaTela);
    clearTimeout(this.temporizadorPrevia);
    this.inscricaoPrevia?.unsubscribe();
  }

  get alterado(): boolean {
    return !!this.modelo && (this.textoModelo || JSON.stringify(this.modelo)) + '|' + this.categoriaId !== this.estadoSalvo;
  }

  get fonteAtual(): any {
    return this.fontes.find((f) => f.chave === this.modelo?.fonte);
  }

  private calc_colunasFonte(): any[] {
    const termo = this.buscaColuna.trim().toLowerCase();
    return (this.fonteAtual?.colunas || []).filter(
      (c: any) => !termo || c.nome.includes(termo) || (c.rotulo || '').toLowerCase().includes(termo)
    );
  }

  definirColunasComValor(colunas: string[]): void {
    if (!colunas?.length) {
      delete this.modelo.filtrosFixos;
      return;
    }
    this.modelo.filtrosFixos = { operador: 'AND', condicoes: colunas.map((coluna) => ({ operador: 'NOT_NULL', coluna })) };
  }

  private calc_colunasComValor(): void {
    const fixos = this.modelo?.filtrosFixos;
    if (!fixos) {
      this.colunasComValor = [];
      this.filtrosFixosProprios = false;
      return;
    }
    const condicoes: any[] = fixos.operador === 'AND' && Array.isArray(fixos.condicoes) ? fixos.condicoes : [fixos];
    const simples = condicoes.every((c) => c && c.operador === 'NOT_NULL' && c.coluna && !c.condicoes);
    this.colunasComValor = simples ? condicoes.map((c) => c.coluna) : [];
    this.filtrosFixosProprios = !simples;
  }

  private porTipo(regras: { [chave: string]: string[] }): { [chave: string]: Opcao[] } {
    const mapa: { [chave: string]: Opcao[] } = {};
    for (const chave of Object.keys(regras)) {
      mapa[chave] = this.opcoesColuna.filter((o) => regras[chave].includes(this.tipoColuna(o.value)));
    }
    return mapa;
  }

  private calc_variaveisFonte(): { nome: string; rotulo: string; descricao: string }[] {
    return (this.fonteAtual?.variaveis || [])
      .filter((v: any) => v.nome && v.total && v.coluna)
      .map((v: any) => ({
        nome: v.nome,
        rotulo: v.rotulo || v.nome,
        descricao: `${NOMES_TOTAL[v.total] || v.total} de ${this.rotuloColuna(v.coluna)}`,
      }));
  }

  variavelNoModelo(nome: string): any {
    return (this.modelo?.variaveis || []).find((v: any) => v.variavel === nome) || null;
  }

  variavelUsada(nome: string): boolean {
    return !!this.variavelNoModelo(nome) || (this.modelo?.indicadores || []).some((i: any) => i.variavel === nome);
  }

  alternarVariavel(nome: string, campo: 'grupo' | 'totalGeral'): void {
    if (!this.podeEditar) {
      return;
    }
    const lista: any[] = this.modelo.variaveis || [];
    let item = lista.find((v) => v.variavel === nome);
    if (!item) {
      item = { variavel: nome };
      lista.push(item);
    }
    if (item[campo]) {
      delete item[campo];
    } else {
      item[campo] = true;
    }
    const restantes = lista.filter((v) => v.grupo || v.totalGeral);
    if (restantes.length) {
      this.modelo.variaveis = restantes;
    } else {
      delete this.modelo.variaveis;
    }
  }

  usarVariavel(v: { nome: string; rotulo: string }): void {
    if (!this.podeEditar) {
      return;
    }
    if (this.aba === 'indicadores') {
      this.modelo.indicadores.push({ id: this.idUnico(this.slug(v.rotulo) || 'variavel'), rotulo: v.rotulo, variavel: v.nome });
      return;
    }
    if (!this.variavelNoModelo(v.nome)) {
      const temGrupo = !!this.modelo.agrupamentos?.length;
      this.modelo.variaveis = [...(this.modelo.variaveis || []), temGrupo ? { variavel: v.nome, grupo: true, totalGeral: true } : { variavel: v.nome, totalGeral: true }];
    }
    if (this.aba !== 'agrupamentos') {
      this.trocarAba('agrupamentos');
    }
  }

  private calc_opcoesColuna(): Opcao[] {
    return (this.fonteAtual?.colunas || []).map((c: any) => ({ label: c.rotulo || c.nome, value: c.nome }));
  }

  get baseAtual(): string {
    return this.service.base?.descricao || '';
  }

  get rotuloSituacao(): string {
    if (!this.info) {
      return '';
    }
    const base = this.info.versaoPublicada ? `v${this.info.versaoPublicada}` : '';
    if (this.info.temRascunho || this.alterado) {
      return base ? `Rascunho sobre a ${base}` : 'Rascunho';
    }
    return base ? `Publicado ${base}` : 'Rascunho';
  }

  get proximaVersao(): number {
    const maior = Math.max(0, ...this.versoes.map((v) => v.versao), this.info?.versaoPublicada || 0, this.info?.versaoPiloto || 0);
    return maior + 1;
  }

  get abaComPainel(): boolean {
    return !['distribuicao', 'versoes'].includes(this.aba);
  }

  get dicaColuna(): string {
    switch (this.aba) {
      case 'filtros':
        return 'Clique numa coluna para criar um filtro por ela.';
      case 'agrupamentos':
        return 'Clique numa coluna para criar um nível de agrupamento.';
      case 'indicadores':
        return 'Clique numa coluna para criar um indicador com a soma dela.';
      case 'graficos':
        return 'Clique numa coluna para criar um gráfico por ela.';
      default:
        return 'Clique numa coluna para mostrá-la no relatório.';
    }
  }

  trocarAba(aba: Aba): void {
    if (this.aba === aba) {
      return;
    }
    if (this.aba === 'json' && this.jsonInvalido) {
      this.messageService.add({ severity: 'warn', summary: 'JSON inválido', detail: 'Corrija o JSON antes de sair da aba: ' + this.jsonInvalido });
      return;
    }
    if (aba === 'json') {
      this.modeloTexto = JSON.stringify(this.modelo, null, 2);
      this.jsonInvalido = '';
    }
    this.aba = aba;
  }

  textoAlterado(texto: string): void {
    this.modeloTexto = texto;
    try {
      const obj = JSON.parse(texto);
      this.jsonInvalido = '';
      this.modelo = this.normalizar(obj);
    } catch (e) {
      this.jsonInvalido = (e as Error).message;
    }
  }

  formatar(): void {
    if (!this.jsonInvalido) {
      this.modeloTexto = JSON.stringify(this.modelo, null, 2);
    }
  }

  rotuloColuna(nome: string): string {
    const c = this.colunaFonte(nome);
    return c?.rotulo || nome;
  }

  tipoColuna(nome: string): string {
    return this.colunaFonte(nome)?.tipo || '';
  }

  colunaFonte(nome: string): any {
    return (this.fonteAtual?.colunas || []).find((c: any) => c.nome === nome);
  }

  parametroFonte(nome: string): any {
    return (this.fonteAtual?.parametros || []).find((p: any) => p.nome === nome);
  }

  usosColuna(nome: string): string[] {
    const usos: string[] = [];
    if (!this.modelo) {
      return usos;
    }
    if (this.modelo.colunas.some((c: any) => c.coluna === nome)) {
      usos.push('coluna');
    }
    if (this.modelo.agrupamentos.some((a: any) => a.coluna === nome)) {
      usos.push('agrupamento');
    }
    if (this.modelo.filtros.some((f: any) => f.coluna === nome)) {
      usos.push('filtro');
    }
    return usos;
  }

  usarColuna(c: any): void {
    if (!this.podeEditar) {
      return;
    }
    switch (this.aba) {
      case 'filtros':
        this.adicionarFiltroColuna(c.nome);
        break;
      case 'agrupamentos':
        this.adicionarNivel(c.nome);
        break;
      case 'indicadores':
        this.adicionarIndicador(c.nome);
        break;
      case 'graficos':
        this.adicionarGrafico(c.nome);
        break;
      default:
        if (this.aba !== 'colunas') {
          this.trocarAba('colunas');
        }
        this.adicionarColuna(c.nome);
    }
  }

  mover(lista: any[], i: number, delta: number): void {
    const j = i + delta;
    if (j < 0 || j >= lista.length) {
      return;
    }
    [lista[i], lista[j]] = [lista[j], lista[i]];
  }

  remover(lista: any[], i: number): void {
    lista.splice(i, 1);
  }

  definir(obj: any, campo: string, valor: any): void {
    if (valor === null || valor === undefined || valor === '' || (Array.isArray(valor) && !valor.length)) {
      delete obj[campo];
    } else {
      obj[campo] = valor;
    }
  }

  adicionarColuna(nome: string): void {
    if (!nome) {
      return;
    }
    if (this.modelo.colunas.some((c: any) => c.coluna === nome)) {
      this.messageService.add({ severity: 'info', summary: 'Já está no relatório', detail: this.rotuloColuna(nome) });
      return;
    }
    const coluna: any = { coluna: nome };
    if (['moeda', 'decimal'].includes(this.tipoColuna(nome))) {
      coluna.total = 'soma';
    }
    this.modelo.colunas.push(coluna);
  }

  private calc_colunasForaDoRelatorio(): Opcao[] {
    return this.opcoesColuna.filter((o) => !this.modelo?.colunas.some((c: any) => c.coluna === o.value));
  }

  adicionarOrdenacao(nome: string): void {
    if (nome && !this.modelo.ordenacao.some((o: any) => o.coluna === nome)) {
      this.modelo.ordenacao.push({ coluna: nome, direcao: 'ASC' });
    }
  }

  private calc_colunasSemOrdenacao(): Opcao[] {
    return this.opcoesColuna.filter((o) => !this.modelo?.ordenacao.some((c: any) => c.coluna === o.value));
  }

  private calc_parametrosForaDosFiltros(): any[] {
    return (this.fonteAtual?.parametros || []).filter(
      (p: any) => p.tipo !== 'empresas' && !this.modelo?.filtros.some((f: any) => f.parametro === p.nome)
    );
  }

  private calc_colunasSemFiltro(): Opcao[] {
    return this.opcoesColuna.filter((o) => !this.modelo?.filtros.some((f: any) => f.coluna === o.value));
  }

  adicionarFiltroParametro(nome: string): void {
    this.modelo.filtros.push({ parametro: nome });
  }

  adicionarFiltroColuna(nome: string): void {
    if (!nome) {
      return;
    }
    if (this.modelo.filtros.some((f: any) => f.coluna === nome)) {
      this.messageService.add({ severity: 'info', summary: 'Já existe filtro por esta coluna', detail: this.rotuloColuna(nome) });
      return;
    }
    const filtro: any = { coluna: nome };
    if (this.tipoColuna(nome) === 'texto') {
      filtro.multiplo = true;
    }
    this.modelo.filtros.push(filtro);
  }

  tipoFiltro(f: any): string {
    if (f.parametro) {
      return this.parametroFonte(f.parametro)?.tipo || '';
    }
    const tipo = this.tipoColuna(f.coluna);
    if (f.multiplo) {
      return tipo === 'texto' ? 'listaTexto' : 'listaInteiro';
    }
    return tipo === 'texto' ? 'texto' : 'faixa:' + tipo;
  }

  rotuloPadraoFiltro(f: any): string {
    return f.parametro ? this.parametroFonte(f.parametro)?.rotulo || f.parametro : this.rotuloColuna(f.coluna);
  }

  rotuloFiltro(f: any): string {
    return f.rotulo || this.rotuloPadraoFiltro(f);
  }

  chaveFiltro(f: any): string {
    return f.parametro || f.coluna;
  }

  obrigatorio(f: any): boolean {
    if (f.obrigatorio !== undefined) {
      return f.obrigatorio;
    }
    const p = f.parametro ? this.parametroFonte(f.parametro) : null;
    return !!p && (!!p.obrigatorio || p.tipo === 'periodo');
  }

  private recalcular(): void {
    const fonte = this.fonteAtual;
    const chaveOpcoes =
      (fonte?.chave || '') + '|' + (fonte?.versao ?? '') + '|' + JSON.stringify(fonte?.colunas || []) + '|' + JSON.stringify(fonte?.variaveis || []) + '|' + this.buscaColuna;
    if (chaveOpcoes !== this.chaveOpcoes) {
      this.chaveOpcoes = chaveOpcoes;
      this.colunasFonte = this.calc_colunasFonte();
      this.opcoesColuna = this.calc_opcoesColuna();
      this.variaveisFonte = this.calc_variaveisFonte();
      this.opcoesVariavel = this.variaveisFonte.map((v) => ({ label: `${v.rotulo} · ${v.descricao}`, value: v.nome }));
      this.opcoesCategoriaGrafico = this.porTipo(CATEGORIA_POR_GRAFICO);
      this.opcoesColunaMetrica = this.porTipo(COLUNA_POR_FUNCAO);
    }
    this.calc_colunasComValor();
    this.colunasForaDoRelatorio = this.calc_colunasForaDoRelatorio();
    this.colunasSemOrdenacao = this.calc_colunasSemOrdenacao();
    this.parametrosForaDosFiltros = this.calc_parametrosForaDosFiltros();
    this.colunasSemFiltro = this.calc_colunasSemFiltro();
    this.opcoesNivel = this.calc_opcoesNivel();
    this.opcoesOrdenarGrupo = this.calc_opcoesOrdenarGrupo();
    const porParametro: { [chave: string]: Opcao[] } = {};
    for (const p of this.fonteAtual?.parametros || []) {
      porParametro[p.nome] = (p.opcoes || []).map((o: string) => ({ label: o, value: o }));
    }
    this.opcoesParametroPor = porParametro;
    this.opcoesIndicadorPor = this.modelo.indicadores.map((i: any) =>
      this.modelo.indicadores.filter((x: any) => x !== i && x.id).map((x: any) => ({ label: x.rotulo || x.id, value: x.id }))
    );
    this.layoutEfetivo = this.calc_layoutEfetivo();
    this.previaGrupos = this.calc_previaGrupos();
  }

  colunaTemporal(nome: string): boolean {
    return ['data', 'dataHora'].includes(this.tipoColuna(nome));
  }

  adicionarGrafico(categoria?: string): void {
    const colunas = this.fonteAtual?.colunas || [];
    const cat = categoria || colunas.find((c: any) => c.tipo === 'texto')?.nome || colunas[0]?.nome;
    if (!cat) {
      return;
    }
    const numerica =
      this.modelo.colunas
        .map((c: any) => this.colunaFonte(c.coluna))
        .find((c: any) => c && ['moeda', 'decimal', 'inteiro'].includes(c.tipo) && c.nome !== cat) ||
      colunas.find((c: any) => ['moeda', 'decimal'].includes(c.tipo) && c.nome !== cat);
    const temporal = this.colunaTemporal(cat);
    const grafico: any = {
      id: this.idUnicoGrafico('grafico_' + cat),
      titulo: (numerica ? numerica.rotulo || numerica.nome : 'Linhas') + ' por ' + this.rotuloColuna(cat).toLowerCase(),
      tipo: temporal ? 'linha' : 'barrasHorizontais',
      categoria: cat,
      metrica: numerica ? { funcao: 'SUM', coluna: numerica.nome } : { funcao: 'COUNT' },
    };
    if (temporal) {
      grafico.agruparData = 'dia';
    }
    this.modelo.graficos.push(grafico);
  }

  categoriasDoGrafico(g: any): Opcao[] {
    return this.opcoesCategoriaGrafico[g.tipo] || this.opcoesColuna;
  }

  colunasDaMetrica(g: any): Opcao[] {
    return this.opcoesColunaMetrica[g.metrica?.funcao] || this.opcoesColuna;
  }

  definirTipoGrafico(g: any, tipo: string): void {
    g.tipo = tipo;
    const validas = this.categoriasDoGrafico(g);
    if (validas.length && !validas.some((o) => o.value === g.categoria)) {
      this.definirCategoriaGrafico(g, validas[0].value);
    }
  }

  definirFuncaoGrafico(g: any, funcao: string): void {
    g.metrica.funcao = funcao;
    const validas = this.colunasDaMetrica(g);
    if (g.metrica.coluna && !validas.some((o) => o.value === g.metrica.coluna)) {
      if (funcao === 'COUNT') {
        delete g.metrica.coluna;
      } else {
        g.metrica.coluna = validas[0]?.value;
      }
    } else if (!g.metrica.coluna && funcao !== 'COUNT' && validas.length) {
      g.metrica.coluna = validas[0].value;
    }
  }

  definirCategoriaGrafico(g: any, nome: string): void {
    g.categoria = nome;
    if (this.colunaTemporal(nome)) {
      g.agruparData = g.agruparData || 'dia';
    } else {
      delete g.agruparData;
    }
  }

  definirLimiteGrafico(g: any, valor: any): void {
    const n = valor === null || valor === undefined || valor === '' ? null : Math.max(1, Math.min(50, Math.round(Number(valor))));
    this.definir(g, 'limite', n);
  }

  temporalGrafico(g: any): boolean {
    return !!g.agruparData || g.tipo === 'linha' || g.tipo === 'area';
  }

  removerGrafico(i: number): void {
    const id = this.modelo.graficos[i]?.id;
    this.modelo.graficos.splice(i, 1);
    this.modelo.layout = this.modelo.layout.filter((b: any) => b.bloco !== 'grafico' || b.grafico !== id);
  }

  private calc_layoutEfetivo(): any[] {
    const lista: any[] = [];
    const usados = new Set<string>();
    const temIndicadores = this.modelo.indicadores.length > 0;
    const graficos = new Map<string, any>(this.modelo.graficos.filter((g: any) => g.id).map((g: any) => [g.id, g]));
    const incluir = (bloco: string, grafico: string, largura: string, inicio = false) => {
      const item = {
        bloco,
        grafico,
        largura: largura || (bloco === 'grafico' ? 'metade' : 'inteira'),
        ...this.descreverBloco(bloco, graficos.get(grafico)),
      };
      if (inicio) {
        lista.unshift(item);
      } else {
        lista.push(item);
      }
    };
    for (const b of this.modelo.layout) {
      const chave = b.bloco + ':' + (b.grafico || '');
      if ((b.bloco === 'indicadores' && !temIndicadores) || (b.bloco === 'grafico' && !graficos.has(b.grafico)) || usados.has(chave)) {
        continue;
      }
      usados.add(chave);
      incluir(b.bloco, b.grafico, b.largura);
    }
    if (temIndicadores && !usados.has('indicadores:')) {
      incluir('indicadores', undefined, 'inteira', true);
    }
    const fora = [...graficos.keys()].filter((id) => !usados.has('grafico:' + id));
    for (const id of fora) {
      incluir('grafico', id, fora.length === 1 ? 'inteira' : 'metade');
    }
    if (!usados.has('tabela:')) {
      incluir('tabela', undefined, 'inteira');
    }
    return lista;
  }

  private descreverBloco(bloco: string, grafico?: any): { rotulo: string; detalhe: string; icone: string } {
    if (bloco === 'indicadores') {
      return { rotulo: 'Indicadores', detalhe: this.modelo.indicadores.map((i: any) => i.rotulo).join(' · '), icone: 'pi-th-large' };
    }
    if (bloco === 'grafico') {
      const icones: { [k: string]: string } = { linha: 'pi-chart-line', area: 'pi-chart-line', pizza: 'pi-chart-pie', rosca: 'pi-chart-pie' };
      return {
        rotulo: grafico?.titulo || grafico?.id,
        detalhe: (NOMES_GRAFICO[grafico?.tipo] || 'Gráfico') + ' por ' + this.rotuloColuna(grafico?.categoria).toLowerCase(),
        icone: icones[grafico?.tipo] || 'pi-chart-bar',
      };
    }
    const grupos = this.modelo.agrupamentos.map((a: any) => a.rotulo || this.rotuloColuna(a.coluna)).join(' › ');
    return { rotulo: 'Tabela', detalhe: this.modelo.colunas.length + ' colunas' + (grupos ? ' · agrupada por ' + grupos : ''), icone: 'pi-table' };
  }

  moverBloco(i: number, delta: number): void {
    const lista = this.layoutEfetivo.map((b) => this.blocoLimpo(b));
    const j = i + delta;
    if (j < 0 || j >= lista.length) {
      return;
    }
    [lista[i], lista[j]] = [lista[j], lista[i]];
    this.modelo.layout = lista;
  }

  definirLargura(i: number, largura: string): void {
    const lista = this.layoutEfetivo.map((b) => this.blocoLimpo(b));
    lista[i].largura = largura;
    this.modelo.layout = lista;
  }

  get posicaoIndicadores(): string {
    const i = this.layoutEfetivo.findIndex((b) => b.bloco === 'indicadores');
    return i > 0 && i === this.layoutEfetivo.length - 1 ? 'final' : 'inicio';
  }

  definirPosicaoIndicadores(posicao: string): void {
    const lista = this.layoutEfetivo.map((b) => this.blocoLimpo(b));
    const i = lista.findIndex((b) => b.bloco === 'indicadores');
    if (i < 0) {
      return;
    }
    const [bloco] = lista.splice(i, 1);
    if (posicao === 'final') {
      lista.push(bloco);
    } else {
      lista.unshift(bloco);
    }
    this.modelo.layout = lista;
  }

  private calc_previaGrupos(): { nivel: number; nome: string; extra: string; icone: string }[] {
    const somadas = this.modelo.colunas.filter((c: any) => c.total).map((c: any) => `${NOMES_TOTAL[c.total] || c.total} de ${(c.rotulo || this.rotuloColuna(c.coluna)).toLowerCase()}`);
    const extra = somadas.length ? somadas.join(', ') : 'sem cálculo';
    const linhas: { nivel: number; nome: string; extra: string; icone: string }[] = [];
    this.modelo.agrupamentos.forEach((a: any, i: number) => {
      linhas.push({ nivel: i, nome: a.rotulo || this.rotuloColuna(a.coluna) || 'Escolha a coluna', extra, icone: 'pi-angle-down' });
    });
    const outras = this.modelo.colunas
      .filter((c: any) => !this.modelo.agrupamentos.some((a: any) => a.coluna === c.coluna))
      .map((c: any) => c.rotulo || this.rotuloColuna(c.coluna));
    linhas.push({
      nivel: this.modelo.agrupamentos.length,
      nome: 'Linhas',
      extra: outras.length ? outras.slice(0, 4).join(' · ') + (outras.length > 4 ? ' …' : '') : '',
      icone: 'pi-minus',
    });
    if (this.totalGeral) {
      linhas.push({ nivel: 0, nome: 'Total geral', extra, icone: 'pi-calculator' });
    }
    return linhas;
  }

  layoutPadrao(): void {
    this.modelo.layout = [];
  }

  private blocoLimpo(b: any): any {
    const bloco: any = { bloco: b.bloco, largura: b.largura };
    if (b.grafico) {
      bloco.grafico = b.grafico;
    }
    return bloco;
  }

  private idUnicoGrafico(base: string, ignorar?: any): string {
    const raiz = this.slug(base) || 'grafico';
    const usados = new Set((this.modelo?.graficos || []).filter((x: any) => x !== ignorar).map((x: any) => x.id));
    let id = raiz;
    let n = 2;
    while (usados.has(id)) {
      id = raiz + '_' + n++;
    }
    return id;
  }

  opcoesParametro(f: any): Opcao[] {
    return (f.parametro && this.opcoesParametroPor[f.parametro]) || this.vazio;
  }

  padraoDaFonte(f: any): string {
    const p = f.parametro ? this.parametroFonte(f.parametro) : null;
    if (!p || p.padrao === null || p.padrao === undefined) {
      return '';
    }
    return this.textoValor(p.padrao, p.tipo);
  }

  textoValor(valor: any, tipo: string): string {
    if (valor === null || valor === undefined || valor === '') {
      return '';
    }
    if (tipo === 'periodo' && typeof valor === 'string') {
      return NOMES_PERIODO[valor] || valor;
    }
    if (Array.isArray(valor)) {
      return valor.join(', ');
    }
    if (typeof valor === 'object') {
      return Object.keys(valor)
        .map((k) => `${k} ${valor[k]}`)
        .join(' · ');
    }
    if (typeof valor === 'boolean') {
      return valor ? 'Sim' : 'Não';
    }
    return String(valor);
  }

  definirPadraoNumero(f: any, valor: any): void {
    this.definir(f, 'padrao', valor === '' || valor === null || valor === undefined ? null : Number(valor));
  }

  private calc_opcoesNivel(): Opcao[] {
    return this.opcoesColuna.filter((o) => !this.modelo?.agrupamentos.some((a: any) => a.coluna === o.value));
  }

  adicionarNivel(nome: string): void {
    if (!nome) {
      return;
    }
    if (this.modelo.agrupamentos.length >= 3) {
      this.messageService.add({ severity: 'warn', summary: 'Até 3 níveis', detail: 'O relatório agrupa em no máximo 3 níveis.' });
      return;
    }
    if (this.modelo.agrupamentos.some((a: any) => a.coluna === nome)) {
      this.messageService.add({ severity: 'info', summary: 'Já é um nível', detail: this.rotuloColuna(nome) });
      return;
    }
    this.modelo.agrupamentos.push({ coluna: nome });
  }

  private calc_opcoesOrdenarGrupo(): Opcao[] {
    const opcoes: Opcao[] = [
      { label: 'Pelo nome do grupo', value: null },
      { label: 'Quantidade de linhas', value: 'qtde' },
    ];
    for (const c of this.modelo?.colunas || []) {
      if (c.total) {
        opcoes.push({ label: `${NOMES_TOTAL[c.total]} de ${this.rotuloColuna(c.coluna)}`, value: `${c.total}:${c.coluna}` });
      }
    }
    return opcoes;
  }

  ordemGrupo(a: any): string {
    return a.ordenarPor?.total || null;
  }

  definirOrdemGrupo(a: any, valor: string): void {
    if (!valor) {
      delete a.ordenarPor;
    } else {
      a.ordenarPor = { total: valor, direcao: a.ordenarPor?.direcao || 'DESC' };
    }
  }

  get totalGeral(): boolean {
    return this.modelo?.totalGeral !== false;
  }

  set totalGeral(valor: boolean) {
    this.definir(this.modelo, 'totalGeral', valor ? null : false);
  }

  adicionarIndicador(nome?: string): void {
    const rotulo = nome ? `Total de ${this.rotuloColuna(nome)}` : 'Linhas';
    const indicador: any = {
      id: this.idUnico(nome ? 'total_' + nome : 'linhas'),
      rotulo,
      metrica: nome ? { funcao: 'SUM', coluna: nome } : { funcao: 'COUNT' },
    };
    this.modelo.indicadores.push(indicador);
  }

  tipoIndicador(i: any): string {
    return i.calculo ? 'calculo' : i.variavel ? 'variavel' : 'metrica';
  }

  definirTipoIndicador(i: any, tipo: string): void {
    delete i.metrica;
    delete i.calculo;
    delete i.variavel;
    if (tipo === 'calculo') {
      i.calculo = { dividir: '', por: '' };
    } else if (tipo === 'variavel') {
      i.variavel = this.variaveisFonte[0]?.nome || '';
    } else {
      i.metrica = { funcao: 'SUM' };
    }
  }

  definirLarguraIndicador(i: any, largura: string): void {
    if (largura === 'metade') {
      i.largura = 'metade';
    } else {
      delete i.largura;
    }
  }

  definirRotuloIndicador(i: any, rotulo: string): void {
    const idAntigo = this.idUnico(this.slug(i.rotulo || ''), i);
    i.rotulo = rotulo;
    if (!i.id || i.id === idAntigo) {
      i.id = this.idUnico(this.slug(rotulo), i);
    }
  }

  alternarSoftware(software: string, marcado: boolean): void {
    const lista = this.distribuicao.softwares.filter((s) => s !== software);
    this.distribuicao.softwares = marcado ? [...lista, software] : lista;
  }

  enviarIa(texto: string): void {
    const modelo = this.ler();
    if (!modelo) {
      return;
    }
    this.mensagensIa = [...this.mensagensIa, { papel: 'usuario', texto }];
    this.historicoIa.push({ papel: 'usuario', texto });
    this.pensandoIa = true;
    this.propostaIa = null;
    this.service.proporIa('ajustar', this.historicoIa, modelo).subscribe(
      (r) => {
        this.pensandoIa = false;
        this.propostaIa = r;
        this.resumoIa = resumirProposta(r.modelo, r.fonte).filter((l) => l.rotulo !== 'Fonte' && l.rotulo !== 'Grão');
        const texto = r.resposta || 'Proposta pronta.';
        this.mensagensIa = [...this.mensagensIa, { papel: 'assistente', texto, avisos: r.avisos, erros: r.erros, rodape: rodapeUso(r) }];
        this.historicoIa.push({ papel: 'assistente', texto });
      },
      (e) => {
        this.pensandoIa = false;
        this.historicoIa.pop();
        this.mensagensIa = [...this.mensagensIa, { papel: 'assistente', texto: this.service.mensagemErro(e) }];
      }
    );
  }

  aplicarIa(): void {
    if (!this.propostaIa?.modelo) {
      return;
    }
    const chave = this.modelo.chave;
    this.modelo = this.normalizar({ ...this.propostaIa.modelo, chave });
    this.modeloTexto = JSON.stringify(this.modelo, null, 2);
    this.jsonInvalido = '';
    this.propostaIa = null;
    this.messageService.add({ severity: 'success', summary: 'Ajuste aplicado no editor', detail: 'Confira e salve o rascunho para manter.' });
  }

  descartarIa(): void {
    this.propostaIa = null;
  }

  abrirPrevia(): void {
    this.mostrarPrevia = true;
    this.rodarPrevia();
  }

  campoPrevia(f: any): string {
    const tipo = this.tipoFiltro(f);
    if (tipo === 'texto' && this.opcoesParametro(f).length) {
      return 'opcoes';
    }
    if (tipo.startsWith('faixa:')) {
      return ['data', 'dataHora'].includes(tipo.substring(6)) ? 'faixaData' : 'faixaNumero';
    }
    return tipo;
  }

  modoPeriodo(chave: string): string {
    const v = this.valoresPrevia[chave];
    return typeof v === 'string' ? v : v && typeof v === 'object' ? 'datas' : null;
  }

  definirModoPeriodo(chave: string, modo: string): void {
    const atual = this.valoresPrevia[chave];
    this.definirPrevia(chave, modo === 'datas' ? (atual && typeof atual === 'object' ? atual : {}) : modo);
  }

  valorPrevia(chave: string, campo?: string): any {
    const v = this.valoresPrevia[chave];
    return campo ? v?.[campo] ?? null : v ?? null;
  }

  definirPrevia(chave: string, valor: any, campo?: string): void {
    if (campo) {
      const anterior = this.valoresPrevia[chave];
      const atual = anterior && typeof anterior === 'object' ? { ...anterior } : {};
      if (valor === null || valor === undefined || valor === '') {
        delete atual[campo];
      } else {
        atual[campo] = valor;
      }
      this.valoresPrevia = { ...this.valoresPrevia, [chave]: atual };
      return;
    }
    const copia = { ...this.valoresPrevia };
    if (valor === null || valor === undefined || valor === '' || (Array.isArray(valor) && !valor.length)) {
      delete copia[chave];
    } else {
      copia[chave] = valor;
    }
    this.valoresPrevia = copia;
  }

  padraoPrevia(f: any): string {
    const texto = this.textoValor(f.padrao, this.tipoFiltro(f)) || this.padraoDaFonte(f);
    return texto ? 'padrão: ' + texto : 'sem filtro';
  }

  limparPrevia(): void {
    this.valoresPrevia = {};
  }

  validar(): void {
    const modelo = this.ler();
    if (!modelo) {
      return;
    }
    this.executar('Validando', this.service.validarModelo(modelo), (v) => {
      this.validacao = v;
      if (v.valida) {
        this.messageService.add({ severity: 'success', summary: 'Modelo válido', detail: v.avisos?.length ? 'Confira os avisos.' : '' });
      }
    });
  }

  rodarPrevia(): void {
    clearTimeout(this.temporizadorPrevia);
    const modelo = this.ler();
    if (!modelo) {
      return;
    }
    if (!this.service.base) {
      this.erroPrevia = 'Escolha a base no topo para rodar a prévia.';
      return;
    }
    if (!modelo.colunas?.length || !this.fonteAtual) {
      this.erroPrevia = !this.fonteAtual ? 'Escolha a fonte na aba Dados.' : 'Mostre ao menos uma coluna.';
      return;
    }
    const sequencia = ++this.sequenciaPrevia;
    this.rodandoPrevia = true;
    this.inscricaoPrevia?.unsubscribe();
    this.inscricaoPrevia = this.service.previaModelo(modelo, this.filtrosPrevia()).subscribe(
      (r) => {
        if (sequencia !== this.sequenciaPrevia) {
          return;
        }
        this.rodandoPrevia = false;
        this.erroPrevia = '';
        this.resultado = r;
        this.validacao = { erros: [], avisos: r.avisos || [] };
      },
      (e) => {
        if (sequencia !== this.sequenciaPrevia) {
          return;
        }
        this.rodandoPrevia = false;
        const corpo = this.service.corpoErro(e);
        if (corpo?.erros?.length) {
          this.erroPrevia = corpo.erros.length === 1 ? 'Corrija antes de rodar a prévia:' : 'Corrija estes pontos antes de rodar a prévia:';
          this.validacao = { erros: corpo.erros, avisos: corpo.avisos || [] };
        } else {
          this.erroPrevia = this.service.mensagemErro(e);
        }
      }
    );
  }

  salvarRascunho(depois?: () => void): void {
    const modelo = this.ler();
    if (!modelo) {
      return;
    }
    this.executar('Salvando rascunho', this.service.salvarRascunhoModelo(this.id, modelo, this.categoriaId), (r) => {
      this.aplicar(r, false);
      if (depois) {
        depois();
      } else {
        this.messageService.add({ severity: 'success', summary: 'Rascunho salvo', detail: 'Os clientes continuam na versão publicada.' });
      }
    });
  }

  abrirPublicar(): void {
    this.publicacao = { piloto: !!this.info?.versaoPublicada, cnpjsPiloto: [...(this.info?.cnpjsPiloto || [])], notas: '' };
    this.mostrarPublicar = true;
  }

  publicar(): void {
    const publicar = () =>
      this.executar(
        'Publicando',
        this.service.publicar(this.id, this.publicacao.piloto, this.publicacao.cnpjsPiloto, this.publicacao.notas),
        (r) => {
          this.mostrarPublicar = false;
          this.aplicar(r, true);
          this.carregarVersoes();
          this.messageService.add({
            severity: 'success',
            summary: 'Publicado',
            detail: this.publicacao.piloto
              ? `A versão ${r.versaoPiloto} vai só para os clientes do piloto.`
              : `A versão ${r.versaoPublicada} vai para quem recebe o modelo na próxima consulta ao catálogo.`,
            life: 7000,
          });
        }
      );
    if (this.podeEditar) {
      this.salvarRascunho(publicar);
    } else {
      publicar();
    }
  }

  abrirParaTodos(): void {
    this.confirmationService.confirm({
      message: `Abrir a v${this.info.versaoPiloto} para todos que recebem o modelo?`,
      accept: () => this.executar('Abrindo para todos', this.service.abrirParaTodos(this.id), (r) => this.aplicar(r, false)),
    });
  }

  encerrarPiloto(): void {
    this.confirmationService.confirm({
      message: 'Encerrar o piloto? Os clientes do piloto voltam para a versão publicada.',
      accept: () => this.executar('Encerrando piloto', this.service.encerrarPiloto(this.id), (r) => this.aplicar(r, false)),
    });
  }

  salvarDistribuicao(): void {
    this.executar('Salvando distribuição', this.service.salvarDistribuicao(this.id, this.distribuicao), (r) => {
      this.aplicar(r, false);
      this.messageService.add({ severity: 'success', summary: 'Distribuição salva', detail: 'Vale na próxima consulta dos clientes ao catálogo.' });
    });
  }

  get resumoDistribuicao(): string {
    const softwares = this.distribuicao.softwares.length ? ' do ' + this.distribuicao.softwares.join(', ') : '';
    switch (this.distribuicao.distribuicao) {
      case 'ROTINAS': {
        const rotinas = this.distribuicao.rotinas.length ? this.distribuicao.rotinas.join(this.distribuicao.exigirTodasRotinas ? ' e ' : ' ou ') : 'nenhuma rotina';
        return `clientes${softwares} com ${rotinas}`;
      }
      case 'CNPJS':
        return `${this.distribuicao.cnpjs.length} ${this.distribuicao.cnpjs.length === 1 ? 'cliente escolhido' : 'clientes escolhidos'}`;
      default:
        return `todos os clientes${softwares}`;
    }
  }

  carregarVersoes(): void {
    this.service.versoesModelo(this.id).subscribe((v) => (this.versoes = v));
  }

  verVersao(v: any): void {
    this.service.versaoModelo(this.id, v.versao).subscribe(
      (r) => {
        this.versaoVista = r;
        this.mostrarVersao = true;
      },
      (e) => this.falha(e)
    );
  }

  get versaoVistaTexto(): string {
    return this.versaoVista ? JSON.stringify(this.versaoVista.modelo, null, 2) : '';
  }

  get modeloAtualTexto(): string {
    return JSON.stringify(this.modelo, null, 2);
  }

  carregarVersaoNoEditor(): void {
    this.modelo = this.normalizar(this.versaoVista.modelo);
    this.modeloTexto = this.versaoVistaTexto;
    this.jsonInvalido = '';
    this.mostrarVersao = false;
    this.messageService.add({ severity: 'info', summary: `Versão ${this.versaoVista.versao} no editor`, detail: 'Salve o rascunho para manter.' });
  }

  republicar(v: any): void {
    this.confirmationService.confirm({
      message: `Voltar os clientes para a v${v.versao}?`,
      accept: () =>
        this.executar('Republicando', this.service.republicar(this.id, v.versao), (r) => {
          this.aplicar(r, false);
          this.carregarVersoes();
        }),
    });
  }

  arquivar(arquivado: boolean): void {
    this.confirmationService.confirm({
      message: arquivado ? 'Arquivar o modelo? Ele sai do catálogo de todos os clientes.' : 'Reativar o modelo?',
      accept: () => this.executar('Salvando', this.service.arquivar(this.id, arquivado), (r) => this.aplicar(r, false)),
    });
  }

  excluir(): void {
    this.confirmationService.confirm({
      message: this.versoes.length
        ? 'Excluir o modelo e todas as versões? Ele some dos clientes em até 1 minuto e não dá para desfazer.'
        : 'Excluir o modelo? Ele nunca foi publicado.',
      accept: () => this.executar('Excluindo', this.service.excluirModelo(this.id), () => this.voltar()),
    });
  }

  abrirFonte(): void {
    const fonte = this.fonteAtual;
    if (fonte && this.service.pode('S')) {
      this.router.navigate(['relatorios-dinamicos/fonte'], { queryParams: { id: fonte.id } });
    }
  }

  voltar(): void {
    this.router.navigate(['relatorios-dinamicos']);
  }

  private filtrosPrevia(): any {
    const filtros: any = {};
    for (const f of this.modelo.filtros || []) {
      const chave = this.chaveFiltro(f);
      const valor = this.valoresPrevia[chave];
      if (valor === null || valor === undefined || valor === '' || (Array.isArray(valor) && !valor.length)) {
        continue;
      }
      const campo = this.campoPrevia(f);
      if (campo === 'periodo') {
        if (typeof valor === 'string') {
          filtros[chave] = valor;
        } else if (valor.inicio && valor.fim) {
          filtros[chave] = { inicio: valor.inicio, fim: valor.fim };
        }
      } else if (campo === 'faixaData' || campo === 'faixaNumero') {
        if (valor.de !== undefined || valor.ate !== undefined) {
          filtros[chave] = valor;
        }
      } else {
        filtros[chave] = valor;
      }
    }
    return filtros;
  }

  private agendarPrevia(espera: number): void {
    clearTimeout(this.temporizadorPrevia);
    if (!this.mostrarPrevia || !this.service.base || this.jsonInvalido) {
      return;
    }
    this.temporizadorPrevia = setTimeout(() => this.rodarPrevia(), espera);
  }

  private carregar(): void {
    this.executar('Carregando', this.service.modelo(this.id), (r) => {
      this.aplicar(r, true);
      this.carregarVersoes();
    });
  }

  private aplicar(r: any, recarregarModelo: boolean): void {
    this.info = r;
    this.categoriaId = r.categoriaId || null;
    this.distribuicao = {
      distribuicao: r.distribuicao || 'TODOS',
      rotinas: r.rotinas || [],
      exigirTodasRotinas: !!r.exigirTodasRotinas,
      cnpjs: r.cnpjs || [],
      softwares: r.softwares || [],
    };
    if (recarregarModelo) {
      this.modelo = this.normalizar(r.modelo || {});
      this.modeloTexto = JSON.stringify(this.modelo, null, 2);
      this.jsonInvalido = '';
    }
    this.textoModelo = JSON.stringify(this.modelo);
    this.estadoSalvo = this.textoModelo + '|' + this.categoriaId;
    this.breadcrumbService.setItems([
      { label: 'Relatórios dinâmicos', routerLink: '/relatorios-dinamicos' },
      { label: r.titulo },
    ]);
  }

  private normalizar(m: any): any {
    const modelo = { ...m };
    for (const lista of ['filtros', 'colunas', 'agrupamentos', 'ordenacao', 'indicadores', 'graficos', 'layout']) {
      if (!Array.isArray(modelo[lista])) {
        modelo[lista] = [];
      }
    }
    return modelo;
  }

  private ler(): any {
    if (this.jsonInvalido) {
      this.messageService.add({ severity: 'warn', summary: 'JSON inválido', detail: this.jsonInvalido });
      return null;
    }
    const copia = JSON.parse(JSON.stringify(this.modelo));
    for (const lista of ['filtros', 'agrupamentos', 'ordenacao', 'indicadores', 'graficos', 'layout']) {
      if (!copia[lista].length) {
        delete copia[lista];
      }
    }
    return copia;
  }

  private idUnico(base: string, ignorar?: any): string {
    const raiz = this.slug(base) || 'indicador';
    const usados = new Set((this.modelo?.indicadores || []).filter((x: any) => x !== ignorar).map((x: any) => x.id));
    let id = raiz;
    let n = 2;
    while (usados.has(id)) {
      id = `${raiz}_${n++}`;
    }
    return id;
  }

  private slug(texto: string): string {
    const s = (texto || '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    return s && /^[0-9]/.test(s) ? 'i_' + s : s;
  }

  private executar<T>(rotulo: string, obs: Observable<T>, ok: (r: T) => void): void {
    this.ocupado = rotulo;
    obs.subscribe(
      (r) => {
        this.ocupado = '';
        ok(r);
      },
      (e) => this.falha(e)
    );
  }

  private falha(e: any): void {
    this.ocupado = '';
    const corpo = this.service.corpoErro(e);
    if (corpo?.erros) {
      this.validacao = { erros: corpo.erros, avisos: corpo.avisos || [] };
    }
    this.messageService.add({ severity: 'error', summary: 'Erro', detail: this.service.mensagemErro(e), life: 10000 });
  }

  private achatarCategorias(categorias: any[]): Opcao[] {
    const opcoes: Opcao[] = [];
    const visitar = (pai: number, prefixo: string) => {
      for (const c of categorias.filter((x) => (x.pai || 0) === pai)) {
        opcoes.push({ label: prefixo + c.nome, value: c.id });
        visitar(c.id, prefixo + c.nome + ' › ');
      }
    };
    visitar(0, '');
    return opcoes;
  }
}
